<script>
  import clickoutside from '@leaflink/stash/directives/clickoutside';
  import Icon from '@leaflink/stash/Icon.vue';
  import { storeToRefs } from 'pinia';
  import { ref } from 'vue';

  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { trackCompanyLicenseChanged } from '@/segment';
  import { switchCompany } from '@/services/api/company';
  import { getLicenseTypeCode } from '@/utils/getLicenseTypeCode';

  import CompanyPickerItem from './CompanyPickerItem';

  const CONTEXT_SWITCHER_STORAGE_KEY = 'll-marketplace-last-session';

  export default {
    name: 'CompanyPicker',

    components: {
      CompanyPickerItem,
      Icon,
    },

    directives: { clickoutside },

    emits: ['navigate'],

    data() {
      return {
        isOpen: false,
      };
    },

    computed: {
      hasOneCompany() {
        return this.companies.length === 1;
      },
    },

    setup() {
      const companyStore = useCompanyStore();
      const { activeCompany } = storeToRefs(companyStore);

      const userStore = useUserStore();
      const { user } = storeToRefs(userStore);

      const isSwitchingCompany = ref(false);

      return {
        activeCompany,
        activeCompanyId: user.value.activeCompanyId,
        companies: user.value.companies,
        isBuyer: user.value.isBuyer,
        isSeller: user.value.isSeller,
        userId: user.value.id,
        isSwitchingCompany,
      };
    },

    methods: {
      toggle() {
        this.isOpen = !this.hasOneCompany ? !this.isOpen : false;
      },

      dismiss() {
        this.isOpen = false;
      },

      companyLink(company) {
        return `/c/${company.slug}`; // TODO Make a vue route for this that handles company reassignment and redirects
      },

      async onCompanyClick(company) {
        if (company.id === this.activeCompanyId) {
          this.dismiss();
          return;
        }

        const parseLicenseInfo = (license) => ({
          number: license.number,
          type: getLicenseTypeCode(license.type),
        });
        // TODO: this will be using the `getPersistentItem` method from @leaflink/stash: https://leaflink.atlassian.net/browse/PLAT-2730
        // then we can remvoe the `ll-` prefix from `CONTEXT_SWITCHER_STORAGE_KEY`
        const prevSessionUrl = localStorage.getItem(CONTEXT_SWITCHER_STORAGE_KEY);

        // remove local storage for session url if it exists
        if (prevSessionUrl) {
          localStorage.removeItem(CONTEXT_SWITCHER_STORAGE_KEY);
        }

        trackCompanyLicenseChanged({
          source: 'Side Nav',
          company_id: this.activeCompanyId,
          group_id: this.activeCompany.group?.id || null,
          is_buyer: this.isBuyer,
          is_seller: this.isSeller,
          user_id: this.userId,
          from: {
            licenses: this.activeCompany.licenses?.map(parseLicenseInfo),
            name: this.activeCompany.name,
          },
          to: {
            licenses: company.licenses?.map(parseLicenseInfo),
            name: company.name,
          },
        });

        try {
          this.isSwitchingCompany = true; // no need to set to false afterwards because of the window.location being changed
          await switchCompany({ companyId: company.id });
        } finally {
          window.location = this.companyLink(company);
          this.$emit('navigate');
        }
      },
    },
  };
</script>

<template>
  <div>
    <div
      v-clickoutside="dismiss"
      :class="[hasOneCompany ? 'cursor-default' : 'cursor-pointer', $style.picker, { [$style['is-open']]: isOpen }]"
    >
      <!-- toggler -->
      <div :class="['pl-2 pr-2 pt-2 pb-1', { 'pr-4': !hasOneCompany }, $style.toggler]" @click="toggle">
        <CompanyPickerItem :company="activeCompany" />
        <Icon v-if="isSwitchingCompany" class="tw-absolute tw-text-ice-500 fx-spin" name="working" />
        <Icon v-else-if="!hasOneCompany" class="tw-absolute tw-text-ice-500" name="caret-down" />
      </div>

      <!-- dropdown content -->
      <div v-if="isOpen && !hasOneCompany" :class="['tw-absolute', $style.dropdown]">
        <ul :class="['ma-0 pa-0', $style.items]">
          <li v-for="company in companies" :class="['pa-1', $style.option]" :key="company.id">
            <a
              :class="['px-1 pt-1 block', $style.link]"
              :href="companyLink(company)"
              @click.prevent="onCompanyClick(company)"
            >
              <CompanyPickerItem :is-active="company.id === activeCompany.id" :company="company" />
            </a>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
  .picker {
    position: relative;
  }

  .dropdown {
    border: 1px solid rgb(255 255 255 / 10%);
    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
    border-top: none;
    max-width: 360px;
    min-width: 200px;
    width: 100%;
    z-index: 200;
  }

  .items {
    list-style-type: none;
    /**
     * Prevents screen overflow with a long list of companies.
     * The Company Picker will be as a Stash component
     * https://leaflink.atlassian.net/browse/STASH-249
     */
    max-height: 450px;
    overflow-y: auto;
  }

  .toggler {
    background-color: $purple-700;
    border: 1px solid rgb(255 255 255 / 10%);
    border-radius: $border-radius;

    .is-open & {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    svg {
      margin-top: -(space(2));
      right: space(1);
      top: 50%;
    }
  }

  .option {
    background-color: $purple-700;

    &:last-of-type {
      border-bottom-left-radius: $border-radius;
      border-bottom-right-radius: $border-radius;
    }
  }

  .link {
    color: inherit;
    text-decoration: none;

    &:hover {
      background: rgb(255 255 255 / 10%);
      border-radius: $border-radius;
      color: inherit;
      text-decoration: none;
    }
  }
</style>
